import { useLazyQuery } from "@apollo/client";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { GridInputs, SearchForm } from "../../../../components/Functional";
import { Button, Spinner } from "../../../../components/ui";
import { FETCH_RESERVATIONS_V2 as fetchReservationsV2Query } from "../../../../graphql/query/Reservation";
import { FETCH_RESERVATION_TYPES } from "../../../../graphql/query/ReservationType";
import { useEntitiesLazy } from "../../../../hooks/useEntities";
import {
  reservationFilterStore,
  useReservationFilters,
} from "../useReservationFilters";
import Instance from "./components/Instance";
import Query from "./components/Query";
import Sort from "./components/Sort";
import Tabs from "./components/Tabs";
import { buildNavigationUrl } from "../../../../util/buildNavigationUrl";

const ReservationSearch = props => {
  const { closeModal, isSearchOpen = false, onSearchSubmitted } = props || {};
  const navigate = useNavigate();
  const location = useLocation();
  const { formik } = useReservationFilters();
  const input = reservationFilterStore.get();
  const [fetchReservationsV2, { loading, data: { reservationsV2 } = {} }] =
    useLazyQuery(
      fetchReservationsV2Query({
        createdBy: {},
        reservationInstance: {},
        reservationUser: {},
        type: {},
      }),
    );

  const [
    fetchReservationTypes,
    { loading: reservationTypesLoading, data: { reservationTypes = [] } = {} },
  ] = useLazyQuery(FETCH_RESERVATION_TYPES);

  const [fetchEntities, { loading: entitiesLoading, entities }] =
    useEntitiesLazy();

  useEffect(() => {
    if (isSearchOpen) {
      fetchReservationTypes();
      fetchEntities();
    }
  }, [isSearchOpen]);

  const RESERVATION_CATEGORY_OPTIONS = [
    { label: "All", value: "" },
    { label: "Reservation", value: 1 },
    { label: "Make Available", value: 2 },
  ];

  const inputs = {
    className: "grid grid-cols-1 gap-4 mt-5",
    inputs: [
      {
        className: "grid grid-cols-1 gap-4 lg:grid-cols-3",
        inputs: [
          {
            label: "Category",
            name: "categoryId",
            type: "select",
            options: RESERVATION_CATEGORY_OPTIONS,
          },
          {
            label: "Type",
            name: "typeId",
            type: "multi-select",
            options: reservationTypes.map(item => ({
              label: item?.description,
              value: item?.id,
            })),
          },
          {
            label: "ID",
            name: "id",
            type: "text",
          },
        ],
      },
      {
        className: "grid grid-cols-1 gap-4 lg:grid-cols-3",
        inputs: [
          {
            label: "Reserved For *",
            name: "reservationUserId",
            type: "multi-select",
            options: entities?.edges?.map(item => ({
              label: item?.name,
              value: item?.id,
            })),
          },
          {
            label: "Date From",
            name: "dateFrom",
            type: "date",
          },
          {
            label: "Date To",
            name: "dateTo",
            type: "date",
          },
        ],
      },
      {
        className: "grid grid-cols-1 gap-4 lg:grid-cols-3",
        inputs: [
          {
            label: "Created By",
            name: "createdById",
            type: "multi-select",
            options: entities?.edges?.map(item => ({
              label: item?.name,
              value: item?.id,
            })),
          },
          {
            label: "End of Reservation from",
            name: "dateDueFrom",
            type: "date",
          },
          {
            label: "End of reservation to",
            name: "dateDueTo",
            type: "date",
          },
        ],
      },
      {
        className: "",
        inputs: [
          {
            label: "Description",
            name: "description",
            type: "text",
          },
        ],
      },
    ],
  };

  const inputProps = {
    formik,
    ...inputs,
  };

  const handleSubmit = e => {
    e?.preventDefault();
    const currentParams = new URLSearchParams(location.search);
    const nextParams = new URLSearchParams();
    ["listId", "imageListId", "workingListId", "imageWorkingListId"].forEach(
      key => {
        const value = currentParams.get(key);
        if (value) {
          nextParams.set(key, value);
        }
      },
    );

    fetchReservationsV2({ variables: { input } }).then(() => {
      navigate(buildNavigationUrl("/reservations", nextParams));
      // Performing a new search invalidates any selection from the previous
      // result set; clear it so stale ids cannot reach bulk actions.
      onSearchSubmitted?.();
      closeModal();
    });
  };

  const searchButtonProps = {
    action: "black",
    label: loading ? "Searching" : "Search",
    disabled: loading,
    type: "submit",
  };

  const resetButtonProps = {
    action: "default",
    label: "Reset",
    onClick: formik?.resetForm,
  };

  const tabComponentProps = {
    reservationsV2,
    formik,
  };

  const tabsProps = {
    formik,
    name: "tab",
    options: [
      {
        label: "Instance",
        value: "instance",
        component: <Instance {...tabComponentProps} />,
      },

      {
        label: "Sort",
        value: "sort",
        component: <Sort {...tabComponentProps} />,
      },
      {
        label: "Query",
        value: "query",
        component: <Query {...tabComponentProps} />,
      },
    ],
  };

  return (
    <SearchForm onSubmit={handleSubmit}>
      <div>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xl font-bold">Reservation Search</div>
          <div className="flex flex-wrap gap-4">
            <Button {...resetButtonProps} />
            <Button {...searchButtonProps} />
          </div>
        </div>
        {reservationTypesLoading || entitiesLoading ? (
          <div className="flex h-[30dvh] w-full items-center justify-center ">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            <GridInputs {...inputProps} />
            <Tabs {...tabsProps} />
          </div>
        )}
      </div>
    </SearchForm>
  );
};

export default ReservationSearch;
